import { h, Fragment } from "preact";
import { useState, useEffect, useMemo, useRef } from "preact/hooks";
import useProduct from "../../helpers/useProduct/useProduct";
import unifyGroupOptions from "../../helpers/unifyGroupOptions";
import unifyGroupVariants from "../../helpers/unifyGroupVariants";
import { IdPrefixContext } from "../../helpers/idPrefixContext";
import ProductOption from "./product-option";
import variantHasOptionValue from "../../helpers/variantHasOptionValue";
import hasStyleCategorySwatches from "../../helpers/hasStyleCategorySwatches";
import { resolveInitialOptionValues } from "../../helpers/resolveInitialVariant";

const optionValueExists = ({ options, optionName, optionValue }) =>
  options.some(
    (option) =>
      option.optionName === optionName &&
      option.optionValues.some(({ name }) => optionValue === name),
  );

const OPTION_ROLES = {
  primary: "primary",
  secondary: "secondary",
};

export default function ProductOptions({
  productId,
  idPrefix,
  initialVariantId,
  preferredOptionValues,
  optionsRole = OPTION_ROLES.primary,
  selectedVariant,
  setSelectedVariant,
  allowMaterialGroups = true,
  truncate = false,
  swatchRequestEnabled,
}) {
  const [productData] = useProduct(productId);
  const ref = useRef(null);

  const variants = useMemo(
    () => unifyGroupVariants(productData),
    [productData],
  );
  const options = useMemo(() => unifyGroupOptions(productData), [productData]);

  const selectionKey = `${productData?.id || ""}:${initialVariantId || ""}`;
  const [selection, setSelection] = useState({
    key: null,
    values: {},
    disabled: {},
  });

  const isCurrentSelection = selection.key === selectionKey;

  const selectedOptionValues = isCurrentSelection
    ? selection.values
    : resolveInitialOptionValues({
        product: productData,
        initialVariantId,
        preferredOptionValues,
      });
  const disabledOptionValues = isCurrentSelection ? selection.disabled : {};

  // Resets during render, not in an effect: an effect would paint the new
  // product's options unselected first, and report a null variant to the parent
  // in the meantime, blanking its price.
  if (!isCurrentSelection) {
    setSelection({
      key: selectionKey,
      values: selectedOptionValues,
      disabled: disabledOptionValues,
    });
  }

  const swatchRequestUrl =
    swatchRequestEnabled === "true" && hasStyleCategorySwatches(productData)
      ? window.productUrl(productData.handle)
      : null;

  const newSelectedVariant =
    variants.find((variant) =>
      variant.selectedOptions.every((selectedOption) => {
        return (
          selectedOptionValues[selectedOption.name] === selectedOption.value
        );
      }),
    ) || null;

  if (newSelectedVariant?.id !== selectedVariant?.id) {
    setSelectedVariant(newSelectedVariant);
  }

  const updateOptionValue = (newValue, optionName) => {
    if (!optionValueExists({ options, optionName, optionValue: newValue }))
      return;

    if (optionsRole === OPTION_ROLES.primary) {
      ref?.current.dispatchEvent(
        new CustomEvent("variant:option-changed", {
          bubbles: true,
          detail: {
            optionName,
            optionValue: newValue,
            optionsRole,
          },
        }),
      );
    }

    const newDisabledOptionValues = {};

    options.forEach((option) => {
      // All values for most recently selected option are valid
      if (option.optionName === optionName) {
        newDisabledOptionValues[option.optionName] = [];
        return;
      }

      newDisabledOptionValues[option.optionName] = option.optionValues
        .filter((optionValue) => {
          return !variants.some((variant) => {
            const hasValue = variantHasOptionValue({
              variant,
              optionName: option.optionName,
              optionValue: optionValue.name,
            });

            const hasLastSelectedValue = variantHasOptionValue({
              variant,
              optionName,
              optionValue: newValue,
            });

            return hasValue && hasLastSelectedValue;
          });
        })
        .map(({ name }) => name);
    });

    setSelection({
      key: selectionKey,
      values: { ...selectedOptionValues, [optionName]: newValue },
      disabled: newDisabledOptionValues,
    });
  };

  useEffect(() => {
    if (!ref || !variants.length || optionsRole !== OPTION_ROLES.primary)
      return;

    const detail = {
      variant: selectedVariant,
      optionsRole,
    };

    ref.current.dispatchEvent(
      new CustomEvent("variant:changed", {
        bubbles: true,
        detail,
      }),
    );
  }, [selectedVariant, variants, ref, optionsRole]);

  return (
    <div
      className={`product-options ${variants.length ? "product-options--loaded" : ""}`}
      ref={ref}
    >
      {selectedVariant && (
        <input
          type="hidden"
          name="id"
          value={window.gidToId(selectedVariant.id)}
        />
      )}
      <IdPrefixContext.Provider value={idPrefix}>
        {options.map((option, i) => (
          <ProductOption
            key={option.optionName}
            option={option}
            updateOptionValue={(newValue) =>
              updateOptionValue(newValue, option.optionName)
            }
            selectedOptionValue={selectedOptionValues[option.optionName]}
            disabledOptionValues={disabledOptionValues[option.optionName]}
            allowMaterialGroups={allowMaterialGroups}
            truncate={truncate}
            sizeGuide={productData?.sizeGuide}
            swatchRequestUrl={swatchRequestUrl}
          />
        ))}
      </IdPrefixContext.Provider>
      {variants.length > 0 && !selectedVariant && (
        <div
          className="product-options__unavailable"
          dangerouslySetInnerHTML={{
            __html: window.localeStrings.product.unavailable_combination,
          }}
        ></div>
      )}
      {variants.length === 0 && (
        <div className="product-options__skeleton"></div>
      )}
    </div>
  );
}
